/**
 * Builds the value list for an exportable enum that mirrors an internal union, keeping
 * the two decoupled while making every drift a compile error whose only fix is the
 * contract-safe one. The exportable model is an OUTPUT contract, so ADDING a produced
 * value is the risky direction (strict consumers and `IN (...)` filters break or
 * silently drop the new category), while a value ceasing to be produced is harmless —
 * but deleting it from the declared schema is not, because already-persisted rows are
 * re-parsed through the enum. Hence:
 *
 * - `current`: values produced today. An internal addition errors until consciously
 *   added here (the contract-change gate); an internal removal errors until moved to...
 * - `retired`: values kept in the declared schema but no longer produced. Errors if the
 *   value reappears internally.
 *
 * The declared enum (`current` + `retired`) only ever widens, one reviewable edit at a
 * time.
 */
export const literalsCovering =
  <Union extends string>() =>
  <const Current extends readonly [Union, ...Union[]], const Retired extends readonly string[] = readonly []>(
    spec: { current: Current; retired?: Retired } & (Exclude<Union, Current[number]> extends never
      ? unknown
      : { current: `MISSING UNION MEMBER: ${Exclude<Union, Current[number]>}` }) &
      (Extract<Retired[number], Union> extends never
        ? unknown
        : { retired: `RETIRED MEMBER IS BACK IN THE UNION: ${Extract<Retired[number], Union>}` }),
  ): readonly [...Current, ...Retired] =>
    // The spread widens to the member union; the tuple shape is guaranteed by construction.
    [...spec.current, ...(spec.retired ?? [])] as unknown as readonly [...Current, ...Retired];
